import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Loader2, KeyRound } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { api, errMsg, fmtDateTime } from "../../lib/api";

const LIMIT_LABELS = {
  max_pages_per_restaurant: "Max website pages per restaurant",
  max_searches_per_restaurant: "Max web searches per restaurant",
  batch_size: "Batch research size (max 10)",
  llm_calls_per_job: "AI calls per research job",
  max_llm_input_chars: "Max AI input characters per job",
  concurrent_jobs: "Concurrent research jobs",
  fetch_timeout_seconds: "Page fetch timeout (seconds)",
  max_contact_pages: "Max contact/about pages",
  max_contact_searches: "Max contact searches",
};

const IntegrationCard = ({ it, onTested }) => {
  const [busy, setBusy] = useState(false);
  const test = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/integrations/${it.key}/test`);
      data.ok ? toast.success(data.detail) : toast.error(data.error);
      onTested();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="rounded-lg border bg-white p-5 shadow-sm" data-testid={`integration-${it.key}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="font-semibold">{it.name}</div>
          <div className="text-sm text-slate-500">{it.purpose}</div>
        </div>
        <span data-testid={`integration-status-${it.key}`} className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ${it.configured ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
          {it.configured ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}{it.status_label ? it.status_label : it.configured ? "Configured" : "NOT CONFIGURED"}
        </span>
      </div>
      <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-xs text-slate-600">
        <span className="flex items-center gap-1"><KeyRound className="h-3.5 w-3.5" />Server secret(s): <span className="font-mono">{it.env.length ? it.env.join(" / ") : "none"}</span></span>
        <span>Last successful test: {fmtDateTime(it.last_success_at)}</span>
        <span>Last tested: {fmtDateTime(it.last_tested_at)}</span>
      </div>
      {it.last_error && <div className="mt-2 rounded-md bg-red-50 p-2 text-xs text-red-700" data-testid={`integration-error-${it.key}`}>{it.last_error}</div>}
      <Button size="sm" variant="outline" className="mt-3" onClick={test} disabled={busy} data-testid={`integration-test-btn-${it.key}`}>{busy && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}Test connection</Button>
    </div>
  );
};

const Limits = () => {
  const [l, setL] = useState(null);
  useEffect(() => {
    api.get("/admin/settings").then((r) => setL(r.data)).catch(() => {});
  }, []);
  const save = async () => {
    try {
      const { data } = await api.put("/admin/settings", l);
      setL(data);
      toast.success("Limits saved");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  if (!l) return null;
  return (
    <div className="rounded-lg border bg-white p-5 shadow-sm" data-testid="limits-card">
      <h2 className="mb-4 text-lg font-bold">Research limits</h2>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {Object.entries(LIMIT_LABELS).map(([k, lab]) => (
          <div key={k} className="space-y-1"><Label className="text-xs">{lab}</Label><Input type="number" value={l[k]} onChange={(e) => setL({ ...l, [k]: Number(e.target.value) })} data-testid={`limit-${k}`} /></div>
        ))}
        <label className="flex items-center gap-2 text-sm"><Switch checked={!!l.measure_mobile_performance} onCheckedChange={(v) => setL({ ...l, measure_mobile_performance: v })} data-testid="limit-measure-mobile" />Measure mobile performance (PageSpeed)</label>
      </div>
      <p className="mt-3 text-xs text-slate-500">AI calls per job includes one retry if the server restarts mid-analysis. Completed stages are never re-run when a job resumes.</p>
      <Button size="sm" className="mt-3 bg-slate-900" onClick={save} data-testid="limits-save-btn">Save limits</Button>
    </div>
  );
};

export default function AdminIntegrations() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/integrations").then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => {
    load();
  }, []);
  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs font-semibold uppercase tracking-wider text-orange-600">Admin</div>
        <h1 className="mt-1 text-3xl font-extrabold tracking-tight">Integrations & limits</h1>
        <p className="mt-1 max-w-3xl text-sm text-slate-500">Credentials are stored only as server-side secrets (backend environment / deployment Secrets) and are never sent to the browser or written to logs. Add or change a key in the server environment, then use Test connection. Missing integrations show Setup required; no demo data is substituted.</p>
      </div>
      <div className="grid gap-4 lg:grid-cols-2">{items.map((it) => <IntegrationCard key={it.key} it={it} onTested={load} />)}</div>
      <Limits />
    </div>
  );
}
